import CustomAvatar from "@/components/CustomAvatar";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getUserID } from "@/lib/auth/auth-server-func";
import { authClient } from "@/lib/better-auth/client";
import { COUNTRIES } from "@/lib/countries";
import { GENDERS } from "@/lib/genders";
import MainLayout from "@/lib/layout/MainLayout";
import { useDeleteAvatar } from "@/lib/tanstack-query/hooks/useDeleteAvatar";
import { useUpdateUser } from "@/lib/tanstack-query/hooks/useUpdateUser";
import { ProfilePasswordSchema } from "@/lib/zod-schemas/profile-password.schema";
import { ProfileSchema } from "@/lib/zod-schemas/profile-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Loader2, Lock, Save, Smile, User, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

export const Route = createFileRoute("/_dashboard/settings")({
  component: RouteComponent,
  beforeLoad: async () => {
    const userID = await getUserID();
    return { userID };
  },
  loader: async ({ context }) => {
    if (!context.userID) {
      throw redirect({ to: "/" });
    }
    return { userID: context.userID };
  },
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteAvatar, isError: isDeleteAvatarError } =
    useDeleteAvatar();
  const { mutateAsync: updateUser, isPending: isPendingUpdateUser } =
    useUpdateUser();
  const { data: userSession, refetch } = authClient.useSession();
  const currentAvatar: string | null | undefined = useMemo(() => {
    return userSession?.user?.avatar;
  }, [userSession?.user?.avatar]);
  const navigate = useNavigate();
  const [provider, setProvider] = useState<string | null>(null);
  const [isPendingChangePassword, setIsPendingChangePassword] = useState(false);

  const profileForm = useForm<z.infer<typeof ProfileSchema>>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: {
      name: userSession?.user?.name ?? "",
      username: userSession?.user?.username ?? "",
      countryCode: userSession?.user?.countryCode ?? "",
      gender: (userSession?.user?.gender as "male" | "female" | "other") ?? "",
    },
  });

  const handleProfileSubmit = async (values: z.infer<typeof ProfileSchema>) => {
    await updateUser(values, {
      onSuccess: (res: any) => {
        if (!res.success && res.field) {
          profileForm.setError(res.field, {
            type: "server",
            message: res.message,
          });
        }
      },
    });
  };

  const passwordForm = useForm<z.infer<typeof ProfilePasswordSchema>>({
    resolver: zodResolver(ProfilePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleDeleteAvatar = async () => {
    await deleteAvatar();
    await refetch();
  };

  const handleChangePassword = async (
    value: z.infer<typeof ProfilePasswordSchema>
  ) => {
    setIsPendingChangePassword(true);
    const { data, error } = await authClient.changePassword({
      newPassword: value.newPassword, // required
      currentPassword: value.currentPassword, // required
      revokeOtherSessions: true,
    });

    if (!error) {
      setIsPendingChangePassword(false);
      passwordForm.reset();

      return;
    }

    if (error.code === "INVALID_PASSWORD") {
      passwordForm.setError("currentPassword", {
        type: "server",
        message: error.message,
      });
    }

    console.log("error:", error);
  };

  useEffect(() => {
    const findProvider = async () => {
      const { data: session } = await authClient.getSession();

      session?.session.provider_id
        ? setProvider(session?.session.provider_id)
        : setProvider("credential");
    };

    findProvider();
  }, []);

  return (
    <>
      <MainLayout.Header headline="Settings"></MainLayout.Header>
      <MainLayout.Content>
        {/* Avatar Section */}
        <div>
          <div className="flex items-center space-x-4">
            <Smile className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Profile Picture
            </h2>
          </div>

          <div className="mt-6 flex items-center space-x-6">
            <div className="relative">
              <div className="flex h-24 w-24 items-center justify-center rounded-full">
                <CustomAvatar
                  width="96px"
                  height="96px"
                  name={userSession?.user.name!}
                  avatarString={currentAvatar}
                  image={userSession?.user.image}
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="button"
                onClick={() => {
                  navigate({ to: "/avatar" });
                }}
              >
                <Smile />
                Change
              </Button>
              <Button
                type="button"
                onClick={() => handleDeleteAvatar()}
                variant={"destructive"}
              >
                <X />
                Remove
              </Button>
            </div>
          </div>
        </div>

        {/* Profile Section */}
        <div>
          <div className="flex items-center space-x-4">
            <User className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Profile
            </h2>
          </div>
          <Form {...profileForm}>
            <form
              onSubmit={profileForm.handleSubmit(handleProfileSubmit)}
              className="mt-6 space-y-4"
            >
              <FormField
                control={profileForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="shad-form_label">Name</FormLabel>
                    <FormControl>
                      <div>
                        <Input
                          className="rounded-xl"
                          placeholder="Type in a name"
                          {...field}
                        />
                        <div className="mt-2 flex items-center justify-between">
                          <FormMessage className="text-red-500" />
                          <span className="text-secondary-text-wu ml-auto text-sm">
                            {field.value.length}/50
                          </span>
                        </div>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={profileForm.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="shad-form_label">Username</FormLabel>
                    <FormControl>
                      <div>
                        <Input
                          className="rounded-xl"
                          placeholder="Type in a username"
                          {...field}
                        />
                        <div className="mt-2 flex items-center justify-between">
                          <FormMessage className="text-red-500" />
                          <span className="text-secondary-text-wu ml-auto text-sm">
                            {field.value.length}/50
                          </span>
                        </div>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={profileForm.control}
                name="countryCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="countryCode">Nationality</FormLabel>
                    <FormControl>
                      <div className="mt-1">
                        <Select {...field} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full rounded-xl">
                              <SelectValue placeholder="Select a country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COUNTRIES.map((country) => (
                              <SelectItem
                                key={country.value}
                                value={country.value}
                              >
                                <span>{country.title}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              <FormField
                control={profileForm.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="gender">Gender</FormLabel>
                    <FormControl>
                      <div className="mt-1">
                        <Select {...field} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full rounded-xl">
                              <SelectValue placeholder="Select a gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {GENDERS.map((gender) => (
                              <SelectItem
                                key={gender.value}
                                value={gender.value}
                              >
                                <span>{gender.title}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-end space-x-4">
                <Button
                  type="button"
                  variant={"outline"}
                  onClick={() => {
                    profileForm.reset();
                  }}
                  className="dark:bg-button-background-wu dark:hover:bg-button-hover-background-wu"
                >
                  Reset
                </Button>
                <Button type="submit">
                  {isPendingUpdateUser ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Save />
                  )}
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </div>

        {/* Password Section */}

        {provider === "credential" && (
          <div className="p-4 dark:text-white">
            <div className="flex items-center space-x-4">
              <Lock className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Change Password
              </h2>
            </div>

            <Form {...passwordForm}>
              <form
                onSubmit={passwordForm.handleSubmit(handleChangePassword)}
                className="mt-6 space-y-4"
              >
                <FormField
                  control={passwordForm.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="shad-form_label">
                        Current Password
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="rounded-xl"
                          type="password"
                          placeholder="Type in your current password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="shad-form_label">
                        New Password
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="rounded-xl"
                          type="password"
                          placeholder="Type in your new password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="shad-form_label">
                        Confirm Password
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="rounded-xl"
                          type="password"
                          placeholder="Confirm your new password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />

                <div className="flex items-center justify-end space-x-4">
                  <Button
                    type="button"
                    variant={"outline"}
                    onClick={() => {
                      passwordForm.reset();
                    }}
                    className="dark:bg-button-background-wu dark:hover:bg-button-hover-background-wu"
                  >
                    Reset
                  </Button>

                  <Button type="submit" className="font-semibold">
                    {isPendingUpdateUser ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Save />
                    )}
                    Save
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </MainLayout.Content>
    </>
  );
}
